import { api } from './apiClient';

export interface Product {
  id: string;
  product_code: string;
  product_name: string;
  description?: string;
  hsn_code?: string;
  unit_of_measure: string;
  category?: string;
  base_price: number;
  purchase_price: number;
  gst_rate: number;
  tax_code_id?: number;
  tax_name?: string;
  tax_code_rate?: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface ProductsResponse {
  products: Product[];
  count: number;
}

interface ProductResponse {
  product: Product;
}

interface CategoriesResponse {
  categories: string[];
}

export const productService = {
  /**
   * Get all products with optional filters
   */
  getProducts: async (params?: {
    isActive?: boolean;
    category?: string;
    hsnCode?: string;
    search?: string;
  }): Promise<ProductsResponse> => {
    return api.get<ProductsResponse>('/products', { params });
  },

  /**
   * Get product by ID
   */
  getProduct: async (id: string): Promise<ProductResponse> => {
    return api.get<ProductResponse>(`/products/${id}`);
  },

  /**
   * Create new product
   */
  createProduct: async (product: Partial<Product>): Promise<ProductResponse> => {
    const payload = {
      productName: product.product_name,
      description: product.description,
      hsnCode: product.hsn_code,
      unitOfMeasure: product.unit_of_measure,
      category: product.category,
      basePrice: product.base_price,
      purchasePrice: product.purchase_price,
      gstRate: product.gst_rate,
      taxCodeId: product.tax_code_id,
      isActive: product.is_active,
    };
    return api.post<ProductResponse>('/products', payload);
  },

  /**
   * Update product
   */
  updateProduct: async (id: string, product: Partial<Product>): Promise<ProductResponse> => {
    const payload = {
      productName: product.product_name,
      description: product.description,
      hsnCode: product.hsn_code,
      unitOfMeasure: product.unit_of_measure,
      category: product.category,
      basePrice: product.base_price,
      purchasePrice: product.purchase_price,
      gstRate: product.gst_rate,
      taxCodeId: product.tax_code_id,
      isActive: product.is_active,
    };
    return api.put<ProductResponse>(`/products/${id}`, payload);
  },

  /**
   * Toggle product active status
   */
  toggleStatus: async (id: string): Promise<ProductResponse> => {
    return api.patch<ProductResponse>(`/products/${id}/toggle-status`);
  },

  /**
   * Get product categories
   */
  getCategories: async (): Promise<CategoriesResponse> => {
    return api.get<CategoriesResponse>('/products/categories');
  },

  /**
   * Search products
   */
  searchProducts: async (term: string): Promise<ProductsResponse> => {
    return api.get<ProductsResponse>('/products/search', { params: { q: term } });
  },
};
